/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-use-before-define */
import React from "react";
// ANT DESIGN COMPONENTS
import { Dropdown, Tooltip, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGear,
  faMinimize,
  faExpand,
  faTableCells,
  faSave,
  faElevator,
  faDesktop,
  faCircleCheck,
  faTerminal,
  faStar,
  faBuilding,
  faCircleInfo,
  faWifi,
  faServer,
  faCircleExclamation,
  faMap,
  faReplyAll,
  faDoorOpen,
  faTriangleExclamation,
  faEyeSlash,
  faEye,
  faBorderAll,
  faArrowDownAZ,
  faArrowDownWideShort,
  faSort,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateEnableDragAndDrop,
  updateShowFullView,
  updateMonitoringGridType,
  updateShowQueueInfo,
  updateSocketConnection,
  updateOpenGlobalTerminal,
  updateOpenModalSaveGrid,
  updateOpenModalManageGrids,
  updateOpenReplayPanel,
  updateDoorUnknownAsOpening,
  updateHideUnconfiguredDoors,
  updateAutoSortGrid,
  updatePreviewFaultServiceMode,
  updateShowCommandDetails,
} from "../../../redux/monitoring/monitoringSlice";
import { updateUserLogged } from "../../../redux/home/homeSlice";
import { usePutUserMutation } from "../../../redux/users/userAPI";
import { usePatchOrganizationSettingsMutation } from "../../../redux/organizations/organizationAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import validatePermission from "../../../utils/validatePermissions";
// COMPONENTS
import OrganizationCascaderSelectMultiple from "../../organizationManagement/organizations/OrganizationCascaderSelectMultiple";
import DecodedFrontEndMessagesModal from "./DecodedFrontEndMessagesModal";
// INTERFACES
import { RootState } from "../../../app/store";
import { MonitoringViewTypeEnum } from "../../../interfaces/Monitoring.interface";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";

function MonitoringHeader() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [openPreDispatchModal, setOpenPreDispatchModal] = React.useState(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    enableDragAndDrop,
    showFullView,
    cascaderSelectMultipleSelectionOptions,
    monitoringGridType,
    showQueueInfo,
    socketConnection,
    idGridSelected,
    openGlobalTerminal,
    openReplayPanel,
    doorUnknownAsOpening,
    hideUnconfiguredDoors,
    showCommandDetails,
  } = useSelector((state: RootState) => state.monitoring);
  const { userLogged, theme } = useSelector((state: RootState) => state.home);
  const { permissions } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [putUser, { isLoading: isLoadingPutUser }] = usePutUserMutation();
  const [patchOrganizationSettings] = usePatchOrganizationSettingsMutation();

  // ************************************************ */
  // DROPDOWN MENU ITEMS **************************** */
  const toolsMenuItems = [
    {
      key: "1",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            if (
              cascaderSelectMultipleSelectionOptions &&
              cascaderSelectMultipleSelectionOptions.length > 0
            ) {
              dispatch(updateOpenModalSaveGrid(true));
            }
          }}
        >
          <span>
            <FontAwesomeIcon icon={faSave} className="generalStyles__mrFix generalStyles__info" />
            {t("monitoring.saveGrid")}
          </span>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            if (
              cascaderSelectMultipleSelectionOptions &&
              cascaderSelectMultipleSelectionOptions.length > 0
            ) {
              dispatch(updateOpenModalManageGrids(true));
            }
          }}
        >
          <span>
            <FontAwesomeIcon icon={faGear} className="generalStyles__mrFix generalStyles__info" />
            {t("monitoring.manageGrids")}
          </span>
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateEnableDragAndDrop(!enableDragAndDrop));
          }}
        >
          {enableDragAndDrop ? (
            <span>
              {" "}
              <FontAwesomeIcon
                icon={faTableCells}
                className="generalStyles__mrFix generalStyles__info"
              />
              {t("monitoring.disableDragAndDrop")}
            </span>
          ) : (
            <span>
              {" "}
              <FontAwesomeIcon
                icon={faTableCells}
                className="generalStyles__mrFix generalStyles__info"
              />
              {t("monitoring.enableDragAndDrop")}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "5",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateOpenGlobalTerminal(!openGlobalTerminal));
            dispatch(updateShowQueueInfo(false));
            dispatch(updateOpenReplayPanel(false));
          }}
        >
          <span>
            {openGlobalTerminal ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faTerminal}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}{" "}
            {openGlobalTerminal ? "Close Terminal" : "Open Terminal"}
          </span>
        </div>
      ),
    },
    {
      key: "6",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateShowQueueInfo(!showQueueInfo));
            dispatch(updateOpenGlobalTerminal(false));
            dispatch(updateOpenReplayPanel(false));
          }}
        >
          <span>
            {showQueueInfo ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faTerminal}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {showQueueInfo ? (
              <>{t("monitoring.hideQueueInfo")}</>
            ) : (
              <>{t("monitoring.showQueueInfo")}</>
            )}
          </span>
        </div>
      ),
    },
    {
      key: "7",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateOpenReplayPanel(!openReplayPanel));
            dispatch(updateOpenGlobalTerminal(false));
            dispatch(updateShowQueueInfo(false));
          }}
        >
          <span>
            {openReplayPanel ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faReplyAll}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {openReplayPanel ? (
              <>{t("general.hideReplayPanel")}</>
            ) : (
              <>{t("general.showReplayPanel")}</>
            )}
          </span>
        </div>
      ),
    },
    {
      key: "8",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={async () => {
            const newValue = !doorUnknownAsOpening;
            dispatch(updateDoorUnknownAsOpening(newValue));
            const token = await GETJwtToken();
            patchOrganizationSettings({
              id: userLogged.org_id,
              token,
              body: { door_unknown_as_opening: newValue },
            });
          }}
        >
          <span>
            {doorUnknownAsOpening ? (
              <FontAwesomeIcon
                icon={faDoorOpen}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {doorUnknownAsOpening ? "Door Opening Mode" : "Door Warning Mode"}
          </span>
        </div>
      ),
    },
    {
      key: "9",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={async () => {
            const newValue = !hideUnconfiguredDoors;
            dispatch(updateHideUnconfiguredDoors(newValue));
            const token = await GETJwtToken();
            patchOrganizationSettings({
              id: userLogged.org_id,
              token,
              body: { hide_unconfigured_doors: newValue },
            });
          }}
        >
          <span>
            {hideUnconfiguredDoors ? (
              <FontAwesomeIcon
                icon={faEyeSlash}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon icon={faEye} className="generalStyles__mrFix generalStyles__info" />
            )}
            {hideUnconfiguredDoors ? "Hide Unconfigured Doors" : "Show All Doors"}
          </span>
        </div>
      ),
    },
    {
      key: "11",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updatePreviewFaultServiceMode(true));
            setTimeout(() => {
              dispatch(updatePreviewFaultServiceMode(false));
            }, 4000);
          }}
        >
          <span>
            <FontAwesomeIcon icon={faEye} className="generalStyles__mrFix generalStyles__info" />
            {t("monitoring.previewFaultServiceMode")}
          </span>
        </div>
      ),
    },
    {
      key: "12",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            setOpenPreDispatchModal(true);
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faTerminal}
              className="generalStyles__mrFix generalStyles__info"
            />
            Open Decoded WS Messages
          </span>
        </div>
      ),
    },
    {
      key: "13",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateShowCommandDetails(!showCommandDetails));
          }}
        >
          <span>
            {showCommandDetails ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {showCommandDetails ? "Hide command details" : "Show command details"}
          </span>
        </div>
      ),
    },
  ];

  const sortMenuItems = [
    {
      key: "sort-1",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateAutoSortGrid("alphabetical"));
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faArrowDownAZ}
              className="generalStyles__mrFix generalStyles__info"
            />
            {t("monitoring.sortAlphabetically")}
          </span>
        </div>
      ),
    },
    {
      key: "sort-2",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateAutoSortGrid("byType"));
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faArrowDownWideShort}
              className="generalStyles__mrFix generalStyles__info"
            />
            {t("monitoring.sortByType")}
          </span>
        </div>
      ),
    },
    {
      key: "sort-3",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateAutoSortGrid("bySize"));
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faBorderAll}
              className="generalStyles__mrFix generalStyles__info"
            />
            {t("monitoring.sortBySize")}
          </span>
        </div>
      ),
    },
    {
      key: "sort-4",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateAutoSortGrid("byTypeAligned"));
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faLayerGroup}
              className="generalStyles__mrFix generalStyles__info"
            />
            {t("monitoring.sortByTypeAligned")}
          </span>
        </div>
      ),
    },
  ];

  const monitorMenuItems = [
    {
      key: "1",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateMonitoringGridType(MonitoringViewTypeEnum.EXPANDED_VIEW));
          }}
        >
          <span>
            {monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faBuilding}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {t("monitoring.expandedView")}
          </span>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateMonitoringGridType(MonitoringViewTypeEnum.OVER_HEAD_VIEW));
          }}
        >
          <span>
            {monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faElevator}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {t("monitoring.overHeadView")}
          </span>
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateMonitoringGridType(MonitoringViewTypeEnum.STATUS_VIEW));
          }}
        >
          <span>
            {monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {t("monitoring.statusView")}
          </span>
        </div>
      ),
    },
    {
      key: "5",
      label: validatePermission("monitor-grid-type-map-view", permissions) ? (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateMonitoringGridType(MonitoringViewTypeEnum.MAP_VIEW));
          }}
        >
          <span>
            {monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon icon={faMap} className="generalStyles__mrFix generalStyles__info" />
            )}
            {t("monitoring.mapView")}
          </span>
        </div>
      ) : null,
    },
  ];

  const connectionMenuItems = [
    {
      key: "1",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            if (process.env.REACT_APP_EDGE_SERVER_CONNECTION === "EDGE") {
              dispatch(updateSocketConnection("EDGE"));
            } else if (process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE") {
              dispatch(updateSocketConnection("OFFLINE"));
            }
          }}
        >
          <span>
            {socketConnection === "EDGE" || socketConnection === "OFFLINE" ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faServer}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {process.env.REACT_APP_EDGE_SERVER_CONNECTION === "EDGE" && "Edge Server"}
            {process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE" && "Offline Server"}
          </span>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateSocketConnection("EMULATOR"));
          }}
        >
          <span>
            {socketConnection === "EMULATOR" ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            Emulator
          </span>
        </div>
      ),
    },
  ];
  // ************************************************ */
  // FUNCTIONS ************************************** */
  const saveFavoriteGrid = async () => {
    // Save favorite theme
    const token = await GETJwtToken();
    const BODY = {
      id: userLogged.id,
      token,
      body: { ...userLogged, pref_grid_id: idGridSelected },
    };
    const response = await putUser(BODY).unwrap();
    if (response && response.data && response.data) {
      dispatch(updateUserLogged(response.data));
    }
  };

  const onClickFullView = () => {
    dispatch(updateShowFullView(!showFullView));
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Spin spinning={isLoadingPutUser}>
      <div className={`generalStyles__spaceBetween mb-3 monitoring__headerContainer__${theme}`}>
        <div className="generalStyles__flex">
          {/** ******************************************* */}
          {/** CASCADER SELECT */}
          <div>
            <OrganizationCascaderSelectMultiple />
          </div>
          {/** ************************************************** */}
          {/** SAVE AS FAVORITE */}
          <div className="generalStyles__mlFix">
            <div
              className="monitoring__barIcon"
              role="button"
              tabIndex={0}
              onKeyDown={emptyFunctionDeclaration}
              onClick={() => {
                saveFavoriteGrid();
              }}
            >
              <Tooltip
                title={
                  userLogged &&
                  userLogged.pref_grid_id &&
                  userLogged.pref_grid_id === idGridSelected
                    ? ""
                    : t("monitoring.saveAsFavorite")
                }
              >
                <FontAwesomeIcon
                  className={`${
                    userLogged &&
                    userLogged.pref_grid_id &&
                    userLogged.pref_grid_id === idGridSelected
                      ? "generalStyles__warning"
                      : `generalStyles__themeIcon1__${theme}`
                  }`}
                  icon={faStar}
                />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="generalStyles__flex">
          {/** ************************************************** */}
          {/** MONITOR ICON */}
          <div>
            <div
              className="monitoring__barIcon"
              role="button"
              tabIndex={0}
              onKeyDown={emptyFunctionDeclaration}
            >
              <Dropdown menu={{ items: monitorMenuItems }} placement="bottomLeft">
                <FontAwesomeIcon
                  className={`generalStyles__themeIcon1__${theme}`}
                  icon={faDesktop}
                />
              </Dropdown>
            </div>
          </div>
          {/** ************************************************** */}
          {/** GEAR ICON */}
          <div>
            <div
              className="monitoring__barIcon"
              role="button"
              tabIndex={0}
              onKeyDown={emptyFunctionDeclaration}
            >
              <Dropdown menu={{ items: toolsMenuItems }} placement="bottomLeft">
                <FontAwesomeIcon className={`generalStyles__themeIcon1__${theme}`} icon={faGear} />
              </Dropdown>
            </div>
          </div>
          {/** ************************************************** */}
          {/** SORT ICON */}
          <div>
            <div
              className="monitoring__barIcon"
              role="button"
              tabIndex={0}
              onKeyDown={emptyFunctionDeclaration}
            >
              <Dropdown menu={{ items: sortMenuItems }} placement="bottomLeft">
                <FontAwesomeIcon className={`generalStyles__themeIcon1__${theme}`} icon={faSort} />
              </Dropdown>
            </div>
          </div>
          {/** ************************************************** */}
          {/** CONNECTION ICON */}
          {validatePermission("monitor-emulator", permissions) && (
            <div>
              <div
                className="monitoring__barIcon"
                role="button"
                tabIndex={0}
                onKeyDown={emptyFunctionDeclaration}
              >
                <Dropdown menu={{ items: connectionMenuItems }} placement="bottomLeft">
                  <FontAwesomeIcon
                    className={`generalStyles__themeIcon1__${theme}`}
                    icon={faWifi}
                  />
                </Dropdown>
              </div>
            </div>
          )}
          {/** ************************************************** */}
          {/** EXPAND ICON */}
          <div>
            <div
              role="button"
              tabIndex={0}
              onClick={onClickFullView}
              onKeyDown={emptyFunctionDeclaration}
              className="monitoring__barIcon"
            >
              <FontAwesomeIcon
                className={`generalStyles__themeIcon1__${theme}`}
                icon={showFullView ? faMinimize : faExpand}
              />
            </div>
          </div>
        </div>
      </div>
      <DecodedFrontEndMessagesModal
        open={openPreDispatchModal}
        onClose={() => setOpenPreDispatchModal(false)}
      />
    </Spin>
  );
}

export default MonitoringHeader;
